import type { Dispatch, FC, SetStateAction } from 'react';
import { useEffect } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import InlineSVG from 'svg-inline-react';

import Marker from 'public/marker-dark.svg';
import { usePlaceDetail } from 'src/app/hooks/data/pharmacy';
import type { PlaceDetail } from 'src/app/types';

import type { PlaceDetailExtended, PlaceDetailsCache } from './ConfirmPharmacy';
import { ListItem, ListSubText, ListTextContainer, PlaceIconContainer } from './styles';

type SearchListItemProps = {
  item: PlaceDetail;
  selectItem: Dispatch<PlaceDetailExtended>;
  setPlaceDetailsCache: Dispatch<SetStateAction<PlaceDetailsCache>>;
  placeDetailsCache: PlaceDetailsCache;
  dayOfTheWeek: number;
  closesAtText: string;
};

export const SearchListItem: FC<SearchListItemProps> = ({
  item,
  selectItem,
  setPlaceDetailsCache,
  placeDetailsCache,
  dayOfTheWeek,
  closesAtText,
}) => {
  const cachedPlaceDetails = placeDetailsCache[item.place_id];
  const { data, refetch, isLoading } = usePlaceDetail(item.place_id);
  const rawHours = data?.opening_hours?.weekday_text?.[dayOfTheWeek - 1];

  // Format hours with translation if available
  // Extract the closing time from the raw hours string (if available)
  const closingTime = rawHours ? rawHours.split(' ').slice(1).join(' ') : '';
  const todayHours = rawHours ? closesAtText.replace('{time}', closingTime) : '';

  useEffect(() => {
    if (!cachedPlaceDetails && !isLoading && data) {
      const updatedCache = placeDetailsCache;
      updatedCache[item.place_id] = data;
      setPlaceDetailsCache(updatedCache);
    }
  }, [data, cachedPlaceDetails]);

  useEffect(() => {
    if (!cachedPlaceDetails) {
      refetch();
    }
  }, [cachedPlaceDetails]);

  return (
    <ListItem
      type="button"
      key={`list-item-${item.place_id}`}
      onClick={() => {
        selectItem({
          ...item,
          hours: todayHours,
        });
      }}
    >
      <PlaceIconContainer aria-hidden>
        <InlineSVG src={Marker} />
      </PlaceIconContainer>
      <ListTextContainer>
        <Typography variant="body">{item.name}</Typography>
        <ListSubText>
          <Typography variant="small">{item.formatted_address}</Typography>
        </ListSubText>
        <ListSubText>
          <Typography variant="small">{todayHours}</Typography>
        </ListSubText>
      </ListTextContainer>
    </ListItem>
  );
};
